import React, { useState, useEffect } from 'react';
import './Wallet.css';
import { useAuth } from '@/hooks/useAuth';
import { Loading, BusinessCardScanner, ContactPreview, ContactDetailView, ConfirmDialog, ExportModal } from '@/components';
import { 
  Wallet as WalletIcon, QrCode, Mail, Phone, Camera,
  Building2, Search, Star, Trash2, Edit, Download, ArrowLeft
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL } from '@/config/constants';
import { useFeedbackStore, useWalletStore } from '@/stores';

interface WalletContact {
  contactId: number;
  displayName: string;
  companyName?: string;
  jobTitle?: string;
  emails?: Array<{ email: string; type: string; isPrimary?: boolean }>;
  phones?: Array<{ number: string; type: string; isPrimary?: boolean }>;
  isFavorite: boolean;
  sourceType: 'qr_scan' | 'physical_card';
  createdAt: string;
  category?: string;
  tags?: string;
}

const Wallet: React.FC = () => {
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Feedback store
  const openFeedback = useFeedbackStore(state => state.openFeedback);

  // Wallet store for cached contacts
  const walletContacts = useWalletStore(state => state.contacts);
  const walletLoading = useWalletStore(state => state.loading);
  const fetchWalletContacts = useWalletStore(state => state.fetchContacts);

  const [contacts, setContacts] = useState<WalletContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [showScanner, setShowScanner] = useState(false);
  const [showContactPreview, setShowContactPreview] = useState(false);
  const [previewContact, setPreviewContact] = useState<any>(null);
  const [ocrMethod, setOcrMethod] = useState<string>('');
  const [ocrConfidence, setOcrConfidence] = useState<number>(0);
  const [savingContact, setSavingContact] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterFavorites, setFilterFavorites] = useState(false);
  const [sortBy, setSortBy] = useState<'recent' | 'az' | 'za'>('recent');
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [filterTag, setFilterTag] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Phase 5: Detail view, edit, delete states
  const [showDetailView, setShowDetailView] = useState(false);
  const [selectedContact, setSelectedContact] = useState<any>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Phase 6: Export states
  const [showExportModal, setShowExportModal] = useState(false);
  // const [selectedForExport, setSelectedForExport] = useState<number[]>([]); // Future: checkbox selection

  useEffect(() => {
    loadContacts();
    if ((location.state as any)?.openScanner) {
      setShowScanner(true);
      window.history.replaceState({}, '');
    }
  }, []);

  // Auto-open a specific contact if navigated from Dashboard recent contacts
  useEffect(() => {
    const selectedId = (location.state as any)?.selectedContactId;
    if (selectedId && contacts.length > 0) {
      const contact = contacts.find(c => String(c.contactId) === String(selectedId));
      if (contact) {
        setSelectedContact(contact);
        setShowDetailView(true);
        window.history.replaceState({}, '');
      }
    }
  }, [contacts, location.state]);

  // Sync local contacts state from store
  useEffect(() => {
    setContacts(walletContacts as WalletContact[]);
    setLoading(walletLoading);
  }, [walletContacts, walletLoading]);

  const loadContacts = async (forceRefresh = false) => {
    try {
      setLoading(true);
      const token = await getAccessToken(true);
      await fetchWalletContacts(token, forceRefresh);
    } catch (err: any) {
      console.error('Failed to load wallet contacts:', err);
      setError(err.message || 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleUnifiedScanSuccess = async (scanResult: any) => {
    try {
      setShowScanner(false);
      setSuccess(null);
      setError(null);

      // scanResult contains the complete response from unified scan endpoint
      // Show preview modal with extracted data
      setPreviewContact(scanResult.data);
      setOcrMethod(scanResult.source || 'unified');
      setOcrConfidence(scanResult.confidence || 0.9);
      setShowContactPreview(true);
    } catch (err: any) {
      console.error('Failed to process scanned card:', err);
      setError(err.message || 'Failed to process business card');
    }
  };

  const handleSaveContact = async (contactData: any, category?: string, tags?: string[]) => {
    try {
      setSavingContact(true);
      const token = await getAccessToken(true);
      
      // Call the create contact endpoint
      const response = await fetch(`${API_BASE_URL}/wallet`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          contactData: {
            ...contactData,
            category: category || 'Other',
            tags: tags || []
          },
          sourceType: 'physical_card'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save contact');
      }

      await response.json();
      setSuccess(`"${contactData.displayName || 'Contact'}" saved successfully!`);
      setShowContactPreview(false);
      
      // Reload contacts
      await loadContacts();

      // Open feedback every 5th successful save
      const saveCount = parseInt(localStorage.getItem('wallet_save_count') || '0', 10) + 1;
      localStorage.setItem('wallet_save_count', String(saveCount));
      if (saveCount % 5 === 0) {
        openFeedback('post_scan');
      }

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Failed to save contact:', err);
      setError(err.message || 'Failed to save contact');
    } finally {
      setSavingContact(false);
    }
  };

  // Phase 5: View contact details
  const handleViewContact = (contact: WalletContact) => {
    setSelectedContact(contact);
    setShowDetailView(true);
  };

  // Phase 5: Edit contact
  const handleEditContact = (contact: any) => {
    setSelectedContact(contact);
    setPreviewContact(contact);
    setIsEditMode(true);
    setShowDetailView(false);
    setShowContactPreview(true);
  };

  // Phase 5: Update contact
  const handleUpdateContact = async (contactData: any, category?: string, tags?: string[]) => {
    if (!selectedContact) return;

    try {
      setSavingContact(true);
      const token = await getAccessToken(true);
      
      // Filter contactData to only include editable fields (exclude read-only metadata)
      const editableContactData = {
        displayName: contactData.displayName,
        givenName: contactData.givenName,
        surname: contactData.surname,
        jobTitle: contactData.jobTitle,
        companyName: contactData.companyName,
        department: contactData.department,
        website: contactData.website,
        notes: contactData.notes,
        emails: contactData.emails || [],
        phones: contactData.phones || [],
        addresses: contactData.addresses || [],
        socialProfiles: contactData.socialProfiles || [],
        category: category || 'Other',
        tags: tags || []
      };
      
      const requestPayload = {
        contactData: editableContactData,
        category: category,
        tags: tags
      };
      
      const response = await fetch(`${API_BASE_URL}/wallet/${selectedContact.contactId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestPayload)
      });

      if (!response.ok) {
        throw new Error('Failed to update contact');
      }

      await response.json();
      setSuccess(`Contact "${contactData.displayName || 'Unknown'}" updated successfully!`);
      setShowContactPreview(false);
      setIsEditMode(false);
      setSelectedContact(null);
      
      // Reload contacts
      await loadContacts(true);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Failed to update contact:', err);
      setError(err.message || 'Failed to update contact');
    } finally {
      setSavingContact(false);
    }
  };

  // Phase 5: Toggle favorite
  const handleToggleFavorite = async (contactId: number) => {
    try {
      // Find the contact to get its name
      const contact = contacts.find(c => c.contactId === contactId);
      const contactName = contact?.displayName || 'Contact';
      
      const token = await getAccessToken(true);
      
      const response = await fetch(`${API_BASE_URL}/wallet/${contactId}/favorite`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to toggle favorite');
      }

      const data = await response.json();
      
      // Safely access the favorite status
      const newFavoriteStatus = data?.data?.isFavorite;
      
      if (newFavoriteStatus !== undefined) {
        // Update local state
        setContacts(contacts.map(c => 
          c.contactId === contactId 
            ? { ...c, isFavorite: newFavoriteStatus }
            : c
        ));

        // Update selected contact if it's the one being toggled
        if (selectedContact && selectedContact.contactId === contactId) {
          setSelectedContact({ ...selectedContact, isFavorite: newFavoriteStatus });
        }

        setSuccess(newFavoriteStatus ? `"${contactName}" added to favorites!` : `"${contactName}" removed from favorites!`);
        setTimeout(() => setSuccess(null), 2000);
      } else {
        // Fallback: reload contacts if we can't get the new status
        await loadContacts(true);
        setSuccess(`"${contactName}" favorite status updated!`);
        setTimeout(() => setSuccess(null), 2000);
      }
    } catch (err: any) {
      console.error('Failed to toggle favorite:', err);
      setError(err.message || 'Failed to update favorite status');
      setTimeout(() => setError(null), 3000);
    }
  };

  // Phase 5: Delete contact (show confirmation)
  const handleDeleteContact = (contactId: number) => {
    setContactToDelete(contactId);
    setShowDeleteConfirm(true);
    setShowDetailView(false);
  };

  // Phase 5: Confirm delete
  const confirmDelete = async () => {
    if (!contactToDelete) return;

    try {
      // Find the contact to get its name before deleting
      const contact = contacts.find(c => c.contactId === contactToDelete);
      const contactName = contact?.displayName || 'Contact';
      
      setIsDeleting(true);
      const token = await getAccessToken(true);
      
      const response = await fetch(`${API_BASE_URL}/wallet/${contactToDelete}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete contact');
      }

      setSuccess(`"${contactName}" deleted successfully!`);
      setShowDeleteConfirm(false);
      setContactToDelete(null);
      setSelectedContact(null);
      
      // Reload contacts
      await loadContacts(true);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Failed to delete contact:', err);
      setError(err.message || 'Failed to delete contact');
    } finally {
      setIsDeleting(false);
    }
  };

  // Phase 6: Export single contact from card
  const handleExportSingleContact = async (contactId: number, displayName: string) => {
    try {
      const token = await getAccessToken(true);
      
      const response = await fetch(`${API_BASE_URL}/wallet/${contactId}/export/vcard`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to export contact');
      }

      // Get the file content
      const blob = await response.blob();
      
      // Create safe filename
      const filename = `${displayName.replace(/[^a-z0-9]/gi, '_')}.vcf`;

      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      setSuccess(`"${displayName}" exported successfully!`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Failed to export contact:', err);
      setError(err.message || 'Failed to export contact');
    }
  };

  // Phase 6: Export contacts
  const handleExport = async (format: 'csv' | 'vcard') => {
    try {
      const token = await getAccessToken(true);

      if (format === 'vcard') {
        // For vCard format, download each contact individually
        let successCount = 0;
        let failedCount = 0;

        for (const contact of contacts) {
          try {
            const response = await fetch(`${API_BASE_URL}/wallet/${contact.contactId}/export/vcard`, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });

            if (!response.ok) {
              console.error(`Failed to export contact: ${contact.displayName}`);
              failedCount++;
              continue;
            }

            // Get the file content
            const blob = await response.blob();
            
            // Create filename from contact name
            const filename = `${contact.displayName.replace(/[^a-z0-9]/gi, '_')}.vcf`;

            // Create download link
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);

            successCount++;

            // Small delay between downloads to avoid browser blocking
            if (successCount < contacts.length) {
              await new Promise(resolve => setTimeout(resolve, 300));
            }
          } catch (err) {
            console.error(`Error exporting ${contact.displayName}:`, err);
            failedCount++;
          }
        }

        if (successCount > 0) {
          setSuccess(`${successCount} contact${successCount !== 1 ? 's' : ''} exported successfully!${failedCount > 0 ? ` (${failedCount} failed)` : ''}`);
          setTimeout(() => setSuccess(null), 3000);
        } else {
          setError('Failed to export contacts');
        }
      } else {
        // For CSV format, export all at once
        const response = await fetch(`${API_BASE_URL}/wallet/export/csv`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to export contacts');
        }

        // Get the file content
        const blob = await response.blob();
        
        // Get filename from Content-Disposition header or create default
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'contacts.csv';
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }

        // Create download link
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);

        setSuccess('All contacts exported successfully!');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err: any) {
      console.error('Failed to export contacts:', err);
      setError(err.message || 'Failed to export contacts');
    }
  };

  // Unique categories/tags from contacts for filter dropdown
  const availableCategories = [...new Set(contacts.map(c => c.category).filter(Boolean))] as string[];
  const availableTags = [...new Set(contacts.flatMap(c => (c.tags || '').split(',').map(t => t.trim())).filter(Boolean))];

  const filteredContacts = contacts
    .filter(contact => {
      const matchesSearch = 
        contact.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (contact.emails && contact.emails[0]?.email?.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesFavorite = !filterFavorites || contact.isFavorite;
      
      const matchesCategory = !filterCategory || contact.category === filterCategory;
      
      const matchesTag = !filterTag || 
        (contact.tags || '').toLowerCase().split(',').map(t => t.trim()).includes(filterTag.toLowerCase());
      
      return matchesSearch && matchesFavorite && matchesCategory && matchesTag;
    })
    .sort((a, b) => {
      if (sortBy === 'az') return (a.displayName || '').localeCompare(b.displayName || '');
      if (sortBy === 'za') return (b.displayName || '').localeCompare(a.displayName || '');
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

  return (
    <div className="wallet-container">
      <div className="wallet-header">
        <button onClick={() => navigate('/')} className="btn-back">
          <ArrowLeft size={20} />
          <span>Back to Dashboard</span>
        </button>
        <div className="header-content">
          <div className="header-title-section">
            <h1>
              <WalletIcon size={32} />
              Business Card Wallet
            </h1>
            <p className="header-description">
              Scan and store business cards you receive from others
            </p>
          </div>
          <button 
            onClick={() => setShowScanner(true)} 
            className="btn-scan-qr"
          >
            <Camera size={20} />
            <span>Scan Business Card</span>
          </button>
        </div>
      </div>

      {/* Alert Messages */}
      {success && (
        <div className="alert alert-success">
          <span>{success}</span>
        </div>
      )}
      
      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Search and Filter Bar */}
      <div className="wallet-controls">
        <div className="search-bar">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search contacts by name, company, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="control-buttons">
          <select
            className="sort-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'recent' | 'az' | 'za')}
          >
            <option value="recent">Recent</option>
            <option value="az">A → Z</option>
            <option value="za">Z → A</option>
          </select>
          {availableCategories.length > 0 && (
            <select
              className="sort-select"
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
            >
              <option value="">All Categories</option>
              {availableCategories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          )}
          {availableTags.length > 0 && (
            <select
              className="sort-select"
              value={filterTag}
              onChange={(e) => setFilterTag(e.target.value)}
            >
              <option value="">All Tags</option>
              {availableTags.map(tag => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </select>
          )}
          <button 
            onClick={() => setFilterFavorites(!filterFavorites)}
            className={`btn-filter ${filterFavorites ? 'active' : ''}`}
          >
            <Star size={18} />
            <span>Favorites</span>
          </button>
          <button 
            onClick={() => setShowExportModal(true)}
            className="btn-export"
            disabled={contacts.length === 0}
            title={contacts.length === 0 ? 'No contacts to export' : 'Export contacts'}
          >
            <Download size={18} />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Contacts count */}
      {contacts.length > 0 && (
        <div className="contacts-count">
          {filteredContacts.length === contacts.length
            ? `Showing ${contacts.length} contact${contacts.length !== 1 ? 's' : ''}`
            : `Showing ${filteredContacts.length} of ${contacts.length} contact${contacts.length !== 1 ? 's' : ''}`
          }
        </div>
      )}

      {/* Contacts List */}
      <div className="wallet-content">
        {loading ? (
          <Loading message="Loading contacts..." />
        ) : filteredContacts.length === 0 ? (
          <div className="empty-state">
            <Camera size={64} />
            <h3>No Contacts Yet</h3>
            <p>Start building your network by scanning business cards</p>
          </div>
        ) : (
          <div className="contacts-grid">
            {filteredContacts.map(contact => (
              <div key={contact.contactId} className="contact-card">
                <div className="contact-header">
                  <button 
                    className="btn-favorite"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleFavorite(contact.contactId);
                    }}
                    title={contact.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                  >
                    <Star 
                      size={18} 
                      fill={contact.isFavorite ? 'currentColor' : 'none'}
                    />
                  </button>
                  <span className="contact-source">
                    {contact.sourceType === 'qr_scan' ? (
                      <><QrCode size={12} /> QR Code</>
                    ) : (
                      <><Camera size={12} /> Scanned Card</>
                    )}
                  </span>
                </div>
                <div className="contact-body" onClick={() => handleViewContact(contact)}>
                  <div className="contact-body-top">
                    <div className="contact-avatar-circle">
                      {(contact.displayName || '?').split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                    </div>
                    <div className="contact-name-section">
                      <h3>{contact.displayName}</h3>
                      {contact.jobTitle && (
                        <p className="contact-title">{contact.jobTitle}</p>
                      )}
                    </div>
                  </div>
                  {(contact.companyName || (contact.emails && contact.emails.length > 0) || (contact.phones && contact.phones.length > 0)) && (
                    <div className="contact-details-divider" />
                  )}
                  {contact.companyName && (
                    <p className="contact-company">
                      <Building2 size={14} />
                      {contact.companyName}
                    </p>
                  )}
                  {contact.emails && contact.emails.length > 0 && (
                    <p className="contact-detail">
                      <Mail size={14} />
                      {contact.emails[0].email}
                    </p>
                  )}
                  {contact.phones && contact.phones.length > 0 && (
                    <p className="contact-detail">
                      <Phone size={14} />
                      {contact.phones[0].number}
                    </p>
                  )}
                  {(contact.category || (contact.tags && contact.tags.trim())) && (
                    <div className="contact-meta">
                      {contact.category && (
                        <span className="category-badge">{contact.category}</span>
                      )}
                      {contact.tags && contact.tags.trim() && (
                        <div className="contact-tags">
                          {contact.tags.split(',').filter(t => t.trim()).map((tag, index) => (
                            <span key={index} className="tag-badge">{tag.trim()}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="contact-footer">
                  <div className="contact-actions">
                    <button 
                      className="btn-icon btn-export" 
                      title="Export vCard"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExportSingleContact(contact.contactId, contact.displayName);
                      }}
                    >
                      <Download size={16} />
                    </button>
                    <button 
                      className="btn-icon" 
                      title="Edit"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditContact(contact);
                      }}
                    >
                      <Edit size={16} />
                    </button>
                    <button 
                      className="btn-icon btn-danger" 
                      title="Delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteContact(contact.contactId);
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Unified Business Card Scanner */}
      <BusinessCardScanner
        isOpen={showScanner}
        onClose={() => setShowScanner(false)}
        onScanSuccess={handleUnifiedScanSuccess}
        getAccessToken={() => getAccessToken(true)}
      />

      {/* Contact Preview Modal (for create and edit) */}
      <ContactPreview
        isOpen={showContactPreview}
        onClose={() => {
          setShowContactPreview(false);
          setIsEditMode(false);
          setSelectedContact(null);
        }}
        contactData={previewContact}
        onSave={isEditMode ? handleUpdateContact : handleSaveContact}
        isLoading={savingContact}
        ocrMethod={ocrMethod}
        confidence={ocrConfidence}
      />

      {/* Contact Detail View Modal */}
      <ContactDetailView
        isOpen={showDetailView}
        onClose={() => {
          setShowDetailView(false);
          setSelectedContact(null);
        }}
        contact={selectedContact}
        onEdit={handleEditContact}
        onDelete={handleDeleteContact}
        onToggleFavorite={handleToggleFavorite}
        isDeleting={isDeleting}
        getAccessToken={() => getAccessToken(true)}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setContactToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Contact?"
        message="Are you sure you want to delete this contact? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isDestructive={true}
        isLoading={isDeleting}
      />

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExport}
        totalContacts={contacts.length}
      />
    </div>
  );
};

export default Wallet;
